<template>
    <div class="ui-select" :class="{ 'ui-select--open': open, 'ui-select--disabled': disabled, 'ui-select--error': !!error }" v-click-outside="close">
        <span v-if="label" class="ui-select__label">{{ label }}</span>
        <div class="ui-select__control" @click="!disabled && onControlClick()">
            <span v-if="multiple && selectedOptions.length" class="ui-select__chips">
                <span v-for="opt in selectedOptions" :key="opt.value" class="ui-select__chip">
                    {{ opt.label }}
                    <button type="button" class="ui-select__chip-remove" @mousedown.stop.prevent="removeValue(opt.value)">
                        <UiIcon name="close" :size="12" />
                    </button>
                </span>
            </span>
            <input
                ref="input"
                class="ui-select__input"
                :placeholder="selectedOptions.length && !multiple ? '' : placeholder"
                :value="multiple ? search : (open ? search : displayValue)"
                :disabled="disabled"
                @input="onSearchInput($event.target.value)"
                @focus="openDropdown"
            />
            <UiSpinner v-if="isLoading" :size="14" />
            <button
                v-else-if="clearable && hasValue"
                type="button"
                class="ui-select__clear"
                @mousedown.stop.prevent="clearValue"
            >
                <UiIcon name="close" :size="14" />
            </button>
            <UiIcon name="expand_more" class="ui-select__caret" :size="16" />
        </div>
        <div v-if="open" class="ui-select__menu">
            <div v-if="filteredOptions.length === 0" class="ui-select__empty">
                {{ isLoading ? 'Cargando...' : (noOptionsText || 'Sin resultados') }}
            </div>
            <button
                v-for="opt in filteredOptions"
                :key="opt.value"
                type="button"
                class="ui-select__option"
                :class="{ 'ui-select__option--selected': isSelected(opt.value) }"
                @mousedown.prevent="selectOption(opt)"
            >
                {{ opt.label }}
            </button>
        </div>
        <span v-if="error" class="ui-select__error">{{ error }}</span>
    </div>
</template>

<script>
import UiIcon from './UiIcon.vue';
import UiSpinner from './UiSpinner.vue';

export default {
    name: 'UiSelect',
    components: { UiIcon, UiSpinner },
    emits: ['update:modelValue', 'search', 'open'],
    directives: {
        clickOutside: {
            mounted(el, binding) {
                el.__clickOutsideHandler__ = (event) => {
                    if (!el.contains(event.target)) binding.value(event);
                };
                document.addEventListener('mousedown', el.__clickOutsideHandler__);
            },
            unmounted(el) {
                document.removeEventListener('mousedown', el.__clickOutsideHandler__);
            },
        },
    },
    props: {
        modelValue: { default: null },
        options: { type: Array, default: () => [] },
        // (search) => Promise<Array<{label, value}>> — if provided, switches to remote mode
        remote: { type: Function, default: null },
        multiple: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
        label: { type: String, default: '' },
        debounceMs: { type: Number, default: 300 },
        // Set when the caller fetches options itself (outside of `remote`) and wants
        // the spinner/"Cargando..." state driven externally.
        loading: { type: Boolean, default: false },
        error: { type: String, default: null },
        noOptionsText: { type: String, default: null },
    },
    data() {
        return {
            open: false,
            search: '',
            remoteOptions: [],
            fetching: false,
            debounceTimer: null,
        };
    },
    computed: {
        isLoading() {
            return this.loading || this.fetching;
        },
        valueArray() {
            if (this.modelValue === null || this.modelValue === undefined) return [];
            return this.multiple ? (Array.isArray(this.modelValue) ? this.modelValue : [this.modelValue]) : [this.modelValue];
        },
        hasValue() {
            return this.valueArray.length > 0;
        },
        allOptions() {
            return this.remote ? this.remoteOptions : this.options;
        },
        selectedOptions() {
            return this.allOptions.filter((opt) => this.valueArray.includes(opt.value));
        },
        displayValue() {
            if (!this.multiple && this.selectedOptions[0]) return this.selectedOptions[0].label;
            return '';
        },
        filteredOptions() {
            if (this.remote) return this.remoteOptions;
            if (!this.search) return this.options;
            const q = this.search.toLowerCase();
            return this.options.filter((opt) => String(opt.label).toLowerCase().includes(q));
        },
    },
    methods: {
        isSelected(value) {
            return this.valueArray.includes(value);
        },
        onControlClick() {
            this.openDropdown();
            this.$refs.input?.focus();
        },
        openDropdown() {
            this.$emit('open');
            if (this.open) return;
            this.open = true;
            this.search = '';
            if (this.remote) this.fetchRemote('');
        },
        close() {
            this.open = false;
            this.search = '';
        },
        onSearchInput(value) {
            this.search = value;
            if (!this.open) this.open = true;
            this.$emit('search', value);
            if (this.remote) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(() => this.fetchRemote(value), this.debounceMs);
            }
        },
        async fetchRemote(search) {
            this.fetching = true;
            try {
                this.remoteOptions = await this.remote(search);
            } finally {
                this.fetching = false;
            }
        },
        selectOption(opt) {
            if (this.multiple) {
                const next = this.isSelected(opt.value)
                    ? this.valueArray.filter((v) => v !== opt.value)
                    : [...this.valueArray, opt.value];
                this.$emit('update:modelValue', next);
                this.search = '';
            } else {
                this.$emit('update:modelValue', opt.value);
                this.close();
            }
        },
        removeValue(value) {
            this.$emit('update:modelValue', this.valueArray.filter((v) => v !== value));
        },
        clearValue() {
            this.$emit('update:modelValue', this.multiple ? [] : null);
        },
    },
};
</script>

<style scoped>
.ui-select {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-family: var(--c-font);
    width: 100%;
}

.ui-select__label {
    font-size: 13px;
    font-weight: 600;
    color: var(--c-text-secondary);
}

.ui-select__control {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-height: 38px;
    border: 1px solid var(--c-border);
    border-radius: var(--c-radius);
    background: #fff;
    padding: 4px 8px;
    cursor: text;
}

.ui-select--open .ui-select__control {
    border-color: var(--c-primary);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.ui-select--disabled .ui-select__control {
    background: var(--c-surface);
    cursor: not-allowed;
}

.ui-select--disabled .ui-select__input {
    cursor: not-allowed;
}

.ui-select--error .ui-select__control {
    border-color: var(--c-danger);
}

.ui-select__error {
    font-size: 12px;
    color: var(--c-danger);
}

.ui-select__input {
    flex: 1;
    min-width: 60px;
    border: none;
    outline: none;
    font-size: 14px;
    color: var(--c-text);
    padding: 4px;
    background: transparent;
}

.ui-select__caret {
    color: var(--c-text-muted);
}

.ui-select__clear {
    border: none;
    background: transparent;
    color: var(--c-text-muted);
    cursor: pointer;
    display: flex;
}

.ui-select__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.ui-select__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--c-primary-soft);
    color: var(--c-primary-hover);
    font-size: 12px;
    font-weight: 600;
    padding: 3px 6px;
    border-radius: var(--c-radius-pill);
}

.ui-select__chip-remove {
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    display: flex;
}

.ui-select__menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid var(--c-border);
    border-radius: var(--c-radius);
    box-shadow: var(--c-shadow-lg);
    max-height: 260px;
    overflow-y: auto;
    z-index: 50;
    padding: 4px;
}

.ui-select__option {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    padding: 8px 10px;
    font-size: 14px;
    color: var(--c-text);
    border-radius: 8px;
    cursor: pointer;
}

.ui-select__option:hover {
    background: var(--c-surface);
}

.ui-select__option--selected {
    background: var(--c-primary-soft);
    color: var(--c-primary-hover);
    font-weight: 600;
}

.ui-select__empty {
    padding: 10px;
    font-size: 13px;
    color: var(--c-text-muted);
}
</style>
